import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { fetchAccountBookings } from "../api/accountApi";

const MAX_BOOKINGS = 10;

const initialState = {
  items: [],
  status: "idle",
  error: null,
  lastFetchedAt: null,
  lastParams: null,
};

const normalizeParams = (payload = {}) => {
  const email = typeof payload.email === "string" ? payload.email.trim() : "";
  const phone = typeof payload.phone === "string" ? payload.phone.trim() : "";
  return { email, phone };
};

const sortBookings = (collection = []) =>
  [...collection].sort(
    (a, b) => new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime(),
  );

export const loadUserBookings = createAsyncThunk(
  "userBookings/load",
  async (payload, { rejectWithValue }) => {
    const { email, phone } = normalizeParams(payload);
    if (!email && !phone) {
      return rejectWithValue("Email or phone is required to fetch bookings.");
    }
    try {
      const response = await fetchAccountBookings({ email, phone });
      const rows = Array.isArray(response?.data) ? response.data : [];
      const sorted = sortBookings(rows);
      const items = sorted.slice(0, MAX_BOOKINGS);
      return { items, fetchedAt: Date.now(), params: { email, phone } };
    } catch (error) {
      return rejectWithValue(error?.message || "Unable to load account bookings.");
    }
  },
);

const userBookingsSlice = createSlice({
  name: "userBookings",
  initialState,
  reducers: {
    clearUserBookings(state) {
      state.items = [];
      state.status = "idle";
      state.error = null;
      state.lastFetchedAt = null;
      state.lastParams = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUserBookings.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
        state.lastParams = normalizeParams(action.meta.arg || {});
      })
      .addCase(loadUserBookings.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload?.items || [];
        state.error = null;
        state.lastFetchedAt = action.payload?.fetchedAt || Date.now();
        state.lastParams = action.payload?.params || state.lastParams;
      })
      .addCase(loadUserBookings.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message || "Unable to load account bookings.";
        state.lastParams = normalizeParams(action.meta.arg || {});
      });
  },
});

const selectUserBookingsState = (state) => state.userBookings || initialState;

export const selectUserBookings = createSelector(
  selectUserBookingsState,
  (state) => state.items || [],
);

export const selectRecentUserBookings = createSelector(selectUserBookings, (items) =>
  Array.isArray(items) ? items.slice(0, MAX_BOOKINGS) : [],
);

export const selectUserBookingsStatus = createSelector(
  selectUserBookingsState,
  (state) => state.status || "idle",
);

export const selectUserBookingsError = createSelector(
  selectUserBookingsState,
  (state) => state.error || null,
);

export const selectUserBookingsParams = createSelector(
  selectUserBookingsState,
  (state) => state.lastParams || null,
);

export const { clearUserBookings } = userBookingsSlice.actions;

export default userBookingsSlice.reducer;
